import { TiTick } from "react-icons/ti";
import { tConv24 } from "utils/dateHelper";
import { FiSearch, FiX, FiPlusCircle, FiTrash } from "react-icons/fi";
import { Button } from "reactstrap";
import { timeDifference, absTimeDifference } from "utils/timeDifference";
import { ProtectedLink } from "components";

export const studentSummaryLessonRecordTableColumns = [
  {
    id: "lessonDate",
    Header: "Lesson Date",
    accessor: (row) => row?.lessonDetail?.date,
    sortable: true,
    filterable: true,
  },
  {
    id: "teacherName",
    Header: "Teacher Name",
    accessor: (row) => row.studentReport?.teacher?.name,
    sortable: true,
    filterable: true,
  },
  {
    id: "duration",
    Header: "Duration",
    accessor: (row) =>
      timeDifference(row.lessonDetail?.startTime, row.lessonDetail?.endTime) /
      60,
    filterable: true,
    sortable: true,
  },
  {
    id: "studentReportAdded",
    Header: "Student Report",
    accessor: (row) =>
      row.studentReportAdded ? (
        <TiTick className="text-success" />
      ) : (
        <FiX className="text-danger" />
      ),
    filterable: true,
  },
  {
    id: "studentAttendanceRecorded",
    Header: "Attendance Recorded",
    accessor: (row) =>
      row.studentAttendanceRecorded ? (
        <TiTick className="text-success" />
      ) : (
        <FiX className="text-danger" />
      ),
    filterable: true,
  },
  {
    id: "studentReportApproved",
    Header: "Student Report Approved",
    accessor: (row) =>
      row.studentReportApproved ? (
        <TiTick className="text-success" />
      ) : (
        <FiX className="text-danger" />
      ),
    filterable: true,
  },
  {
    id: "actions",
    Header: "actions",
    accessor: "actions",
    sortable: false,
    filterable: false,
  },
];

export const studentCourseColumns = (action) => [
  { header: "Course Name", accessor: (row) => row.courseDetail?.name },
  {
    header: "Hours Purchased",
    alignment: "right",
    accessor: (row) =>
      row?.coursePurchaseRecords.reduce(
        (prev, curr) => prev + curr.hoursPurchased,
        0,
      ),
  },
  {
    header: "Hours Finished",
    alignment: "right",
    accessor: (row) =>
      row?.coursePurchaseRecords.reduce(
        (prev, curr) => prev + curr.hoursPurchased,
        0,
      ) - row.hoursLeft,
  },
  { header: "Balance Hours", alignment: "right", accessor: "hoursLeft" },
  {
    header: "Balance Collectible",
    alignment: "right",
    accessor: (row) => {
      const balance = row?.coursePurchaseRecords.reduce(
        (bufferTotal, currCoursePurchaseRecord) =>
          bufferTotal +
          currCoursePurchaseRecord?.totalPricePaid -
          currCoursePurchaseRecord?.paymentRecords?.reduce(
            (bufferPaymentRecordsTotal, currPaymentRecord) =>
              bufferPaymentRecordsTotal + currPaymentRecord?.amountPaid,
            0,
          ),
        0,
      );
      return (
        <div className={balance > 0 ? "text-danger" : "text-success"}>
          {balance > 0 ? balance : "Fully Paid"}
        </div>
      );
    },
  },
  {
    header: "Show",
    action: (row) => (
      <FiSearch
        size={20}
        className="text-warning"
        onClick={() => action(row)}
        style={{ cursor: "pointer" }}
      />
    ),
    alignment: "center",
  },
];

export const studentPmrColumns = (action) => [
  {
    header: "",
    alignment: "center",
    width: 30,
    accessor: (row) => (
      <FiTrash
        size={20}
        className="text-danger"
        onClick={() => action(row)}
        style={{ cursor: "pointer" }}
      />
    ),
  },
  { header: "Date", accessor: (row) => row.date },
  {
    header: "Subject",
    accessor: (row) => row.studentCourse?.courseDetail?.name ?? "None",
  },
  {
    header: "Teacher",
    accessor: (row) => row.createdBy?.fullName,
  },
  {
    header: "Remarks",
    accessor: (row) => row.remarks || <span className="text-muted">None</span>,
  },
  {
    header: "Notes",
    width: 50,
    alignment: "center",
    accessor: (row) => (
      <ProtectedLink
        href={row.notesPath}
        target="_blank"
        label={
          <FiSearch
            size={20}
            className="text-warning"
            style={{ cursor: "pointer" }}
          />
        }
      />
    ),
  },
];

export const studentCoursePurchaseRecordsColumns = ({
  addPayment,
  editHours,
}) => [
  { header: "Received Date", accessor: (row) => row.createdAt },
  { header: "Hours", accessor: (row) => row.hoursPurchased },
  {
    header: "Trial Class",
    accessor: (row) => (row.isTrialClass ? "Yes" : "No"),
  },
  {
    header: "Deposit Date",
    accessor: (row) =>
      row.depositDate || (
        <span className="text-muted font-weight-bold">None</span>
      ),
  },
  {
    header: "Balance Collectible",
    accessor: (row) => {
      const balance =
        row?.totalPricePaid -
        row?.paymentRecords?.reduce(
          (bufferPaymentRecordsTotal, currPaymentRecord) =>
            bufferPaymentRecordsTotal + currPaymentRecord?.amountPaid,
          0,
        );
      return (
        <div className={balance > 0 ? "text-danger" : "text-success"}>
          {balance > 0 ? balance : "Fully Paid"}
        </div>
      );
    },
  },
  {
    header: "Original Course Price",
    accessor: (row) => row.originalCoursePrice,
    alignment: "right",
  },
  {
    header: "Previous Deposit",
    accessor: (row) =>
      row.previousDeposit || <span className="text-muted">None</span>,
    alignment: "right",
  },
  {
    header: "Material Price",
    accessor: (row) =>
      row.materialPrice || <span className="text-muted">None</span>,
    alignment: "right",
  },
  {
    header: "Membership Fee",
    accessor: (row) => row.membershipFee,
    alignment: "right",
  },
  {
    header: "Discount",
    accessor: (row) =>
      row.originalCoursePrice +
      row.membershipFee -
      row.totalPricePaid +
      row.materialPrice -
      row.previousDeposit,
    alignment: "right",
  },
  {
    header: "Total Price",
    accessor: (row) => row.totalPricePaid,
    alignment: "right",
  },
  {
    header: "Total Price Paid",
    accessor: (row) =>
      row?.paymentRecords?.reduce(
        (total, currPaymentRecord) => total + currPaymentRecord.amountPaid,
        0,
      ),
  },
  {
    header: "Add Payment",
    action: (row) =>
      row?.totalPricePaid -
      row?.paymentRecords?.reduce(
        (total, currPaymentRecord) => total + currPaymentRecord.amountPaid,
        0,
      ) ? (
        <FiPlusCircle
          size={20}
          className="text-warning"
          onClick={() => addPayment(row)}
          style={{ cursor: "pointer" }}
        />
      ) : (
        <div className="text-success">Fully Paid</div>
      ),
    alignment: "center",
  },
];

export const studentRecordColumns = ({ showLessonDetailModal }) => [
  {
    header: "Date",
    accessor: (row) => row?.date,
  },
  // {
  //   header: "Centre",
  //   accessor: (row) => row?.studentLessonRecord?.lessonDetail?.centre,
  // },
  {
    header: "Duration (hours)",
    accessor: (row) => row?.hoursDeducted,
    alignment: "right",
  },
  {
    header: "Hours Remaining",
    accessor: (row) => row?.newHoursLeft,
    alignment: "right",
  },
  // {
  //   header: "Student Report",
  //   accessor: (row) =>
  //     row?.studentLessonRecord?.studentReportAdded ? (
  //       <TiTick className="text-success" />
  //     ) : (
  //       <FiX className="text-danger" />
  //     ),
  //   alignment: "center",
  // },
  // {
  //   header: "Show",
  //   action: (row) =>
  //     row?.studentLessonRecord?.studentReportAdded ? (
  //       <FiSearch
  //         size={20}
  //         className="text-warning"
  //         onClick={() => showLessonDetailModal(row)}
  //         style={{ cursor: "pointer" }}
  //       />
  //     ) : (
  //       <FiSearch className="text-muted" size={20} />
  //     ),
  //   alignment: "center",
  // },
];

export const action = (callback) => (row) => (
  <div className="actions-center">
    <Button
      onClick={() => callback(row)}
      color="warning"
      size="sm"
      className="btn-icon btn-link remove"
    >
      <FiSearch size={20} />
    </Button>
  </div>
);

export const lessonDetailColumns = [
  {
    id: "date",
    Header: "Date",
    accessor: (row) =>
      new Date(row?.lessonDetail?.date).toISOString().split("T")[0],
    sortable: true,
    filterable: true,
  },
  {
    id: "courseName",
    Header: "Course",
    accessor: (row) => row?.lessonDetail?.course?.name,
    sortable: true,
    filterable: true,
  },
  {
    id: "duration",
    Header: "Duration",
    accessor: (row) =>
      `${(absTimeDifference(row?.lessonDetail?.startTime, row?.lessonDetail?.endTime) / 60).toFixed(2)}h`,
    sortable: true,
    filterable: true,
  },
  {
    id: "homeworkTime",
    Header: "Homework Time",
    accessor: (row) => (row?.homeworkTime ? "Yes" : "No"),
    sortable: true,
    filterable: true,
  },
  {
    id: "lesson",
    Header: "Lesson",
    accessor: (row) => row?.lessonDetail?.lesson?.name,
    sortable: true,
    filterable: true,
  },
  {
    id: "teacher",
    Header: "Teacher",
    accessor: (row) => row?.lessonDetail?.teacher?.fullName,
    sortable: true,
    filterable: true,
  },
  {
    id: "actions",
    Header: "actions",
    accessor: "actions",
    sortable: false,
    filterable: false,
  },
];

export const studentLessonRecordTableActions =
  ({ showModal, canAdd = false }) =>
  (row) => (
    <div className="actions-right">
      <Button
        color="warning"
        size="sm"
        className={`btn-icon btn-link edit my-0 ${!row.studentReportAdded}`}
        onClick={() =>
          ((canAdd &&
            row.studentAttendanceRecorded &&
            row.lessonDetail?.teacherAttendanceRecorded) ||
            row.studentReportAdded) &&
          showModal(row)
        }
      >
        {row.studentReportAdded ? (
          <FiSearch size={20} />
        ) : canAdd &&
          row.studentAttendanceRecorded &&
          row.lessonDetail?.teacherAttendanceRecorded ? (
          <FiPlusCircle size={20} />
        ) : (
          <FiSearch size={20} className="text-muted" />
        )}
      </Button>
    </div>
  );

export const studentLessonRecordTableColumns = (action) => [
  {
    id: "actions",
    header: "View",
    accessor: (row) => action(row),
    sortable: false,
    filterable: false,
  },
  {
    id: "lessonDate",
    header: "Lesson Date",
    accessor: (row) => row.lessonDetail?.date,
    sortable: true,
    filterable: true,
  },
  {
    id: "lessonCentre",
    header: "Centre",
    accessor: (row) => row.lessonDetail?.centre,
    sortable: true,
    filterable: true,
  },
  {
    id: "lessonTime",
    header: "Start",
    accessor: (row) => tConv24(row.lessonDetail?.startTime),
    sortable: true,
    filterable: true,
  },
  {
    id: "lessonEndTime",
    header: "End",
    accessor: (row) => tConv24(row.lessonDetail?.endTime),
    sortable: true,
    filterable: true,
  },
  {
    id: "studentName",
    header: "Student Name",
    accessor: (row) =>
      row.studentDetail?.firstName + " " + row.studentDetail?.lastName,
    sortable: true,
    filterable: true,
  },
  {
    id: "subject",
    header: "Subject",
    accessor: (row) => row.lessonDetail?.course?.name,
  },
  {
    id: "teacher",
    header: "Teacher",
    accessor: (row) => row.lessonDetail?.teacher?.fullName,
  },
  {
    id: "lesson",
    header: "Lesson",
    accessor: (row) => row.lessonDetail?.lesson?.name,
  },
  {
    id: "Report",
    header: "Report",
    accessor: (row) =>
      row.studentReportAdded ? (
        <TiTick className="text-success" />
      ) : (
        <FiX className="text-danger" />
      ),
    filterable: true,
  },
  {
    id: "Attendance",
    header: "Attendance",
    accessor: (row) =>
      row.studentAttendanceRecorded ? (
        <TiTick className="text-success" />
      ) : (
        <FiX className="text-danger" />
      ),
    filterable: true,
  },
  {
    id: "Approved",
    header: "Approved",
    accessor: (row) =>
      row.studentReportApproved ? (
        <TiTick className="text-success" />
      ) : (
        <FiX className="text-danger" />
      ),
    filterable: true,
  },
  {
    id: "Classwork",
    header: "Classwork",
    accessor: (row) =>
      row?.lessonFiles?.some((file) => file.type === "classwork") ? (
        <TiTick className="text-success" />
      ) : (
        <FiX className="text-danger" />
      ),
    filterable: true,
  },
  {
    id: "In Class Images",
    header: "In Class Images",
    accessor: (row) =>
      row?.lessonFiles?.some((file) => file.type === "in-class") ? (
        <TiTick className="text-success" />
      ) : (
        <FiX className="text-danger" />
      ),
    filterable: true,
  },
];
